import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkGenerationModal = ({ onClose, onSuccess }) => {
  const { currentTenant } = useAuth();
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [barcodeType, setBarcodeType] = useState('qr_code');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    fetchMembersWithoutBarcodes();
  }, [currentTenant?.id]);

  const fetchMembersWithoutBarcodes = async () => {
    setLoading(true);
    try {
      const { data: allMembers, error: membersError } = await supabase
        ?.from('tenant_members')
        ?.select(`
          *,
          user_profiles!tenant_members_user_id_fkey(id, full_name, email, avatar_url)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true);

      if (membersError) throw membersError;

      const { data: existingBarcodes } = await supabase
        ?.from('member_barcodes')
        ?.select('user_id')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true);

      const existingUserIds = new Set(existingBarcodes?.map(b => b?.user_id) || []);
      const availableMembers = allMembers?.filter(m => !existingUserIds?.has(m?.user_profiles?.id)) || [];

      setMembers(availableMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedMembers?.length === members?.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members?.map(m => m?.user_profiles?.id));
    }
  };

  const handleToggleMember = (userId) => {
    setSelectedMembers(prev =>
      prev?.includes(userId)
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleBulkGenerate = async () => {
    setGenerating(true);
    setProgress({ current: 0, total: selectedMembers?.length });

    try {
      const barcodes = selectedMembers?.map(userId => ({
        tenant_id: currentTenant?.id,
        user_id: userId,
        barcode_data: `GYM-${currentTenant?.id?.substring(0, 8)}-${userId?.substring(0, 8)}-${Date.now()}-${Math.random()?.toString(36)?.substring(7)}`,
        barcode_type: barcodeType,
        is_active: true
      }));

      // Insert in batches of 10
      const batchSize = 10;
      for (let i = 0; i < barcodes?.length; i += batchSize) {
        const batch = barcodes?.slice(i, i + batchSize);
        const { error } = await supabase
          ?.from('member_barcodes')
          ?.insert(batch);

        if (error) throw error;
        setProgress({ current: Math.min(i + batchSize, barcodes?.length), total: barcodes?.length });
      }

      onSuccess();
    } catch (error) {
      console.error('Error generating barcodes:', error);
      alert('Failed to generate some barcodes. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-bold">Bulk Barcode Generation</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={generating}
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Barcode Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Barcode Type
            </label>
            <div className="flex gap-3">
              {['qr_code', 'barcode', 'nfc']?.map(type => (
                <button
                  key={type}
                  onClick={() => setBarcodeType(type)}
                  className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                    barcodeType === type
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  }`}
                  disabled={generating}
                >
                  <p className="font-medium capitalize">{type?.replace('_', ' ')}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Member Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium">
                Select Members ({selectedMembers?.length} of {members?.length})
              </label>
              <Button
                onClick={handleSelectAll}
                variant="ghost"
                size="sm"
                disabled={loading || generating}
              >
                {selectedMembers?.length === members?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Icon name="Loader" size={32} className="animate-spin mx-auto text-muted-foreground" />
              </div>
            ) : members?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="CheckCircle" size={48} className="mx-auto mb-3 text-success" />
                <p className="text-muted-foreground">
                  All active members already have barcodes
                </p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-2 border border-border rounded-lg p-3">
                {members?.map(member => (
                  <label
                    key={member?.user_profiles?.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                  >
                    <Checkbox
                      checked={selectedMembers?.includes(member?.user_profiles?.id)}
                      onChange={() => handleToggleMember(member?.user_profiles?.id)}
                      disabled={generating}
                    />
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                      {member?.user_profiles?.avatar_url ? (
                        <img
                          src={member?.user_profiles?.avatar_url}
                          alt={member?.user_profiles?.full_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Icon name="User" size={20} className="text-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{member?.user_profiles?.full_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {member?.user_profiles?.email}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {generating && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Generating barcodes...</span>
                <span className="text-sm text-muted-foreground">
                  {progress?.current} / {progress?.total}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${(progress?.current / progress?.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkGenerate}
            className="flex-1"
            disabled={selectedMembers?.length === 0 || generating}
            loading={generating}
            iconName="Zap"
          >
            Generate {selectedMembers?.length} Barcodes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkGenerationModal;